import * as Logger from "js-logger";
import * as PIXI from 'pixi.js';
import {Spine} from 'pixi-spine';
import GameLoop from "slotworks/core/game-loop";
import ButtonElement from "slotworks/graphics/elements/button-element";
import Layers from "slotworks/graphics/layers/layers";
import RevolverBuilders from "slotworks/integration/revolver/revolver-builders";
import { RevolverInit, RevolverReady} from "slotworks/integration/revolver/revolver-setup";
import BuilderManager from "slotworks/server/builders/builder-manager";
import SlotWorks from "slotworks/slotworks";
import SpinRecordBuilder from "slotworks/integration/revolver/builders/records/spin-record-builder";
import HiddenButtonBehaviour from "slotworks/smart-button/behaviours/hidden-button-behaviour";
import SmartButton from "slotworks/smart-button/smart-button";
import { SmartButtonFlag } from "slotworks/smart-button/smart-button";
import StateMachine from "slotworks/state-machine/state-machine";
import StateRegister from "slotworks/state-machine/state-register";
import { EnableOnScreenErrors } from "slotworks/utils/debug";
import BannerComponent from "slotworks/components/banner/banner-component";
import deviceInfo from "slotworks/utils/device-info";
import revolverModel from "slotworks/integration/revolver/model/revolver-model";
import Components from "slotworks/components/components";
import graphicsManager from "slotworks/graphics/graphics-manager";
import campaignsModel from "slotworks/integration/revolver/model/campaigns-model";
import gameState from "slotworks/model/game-state";
import ShowWinLines from "slotworks/state-machine/standard/states/show-win-lines";
import BigWinComponent from "slotworks/components/bigwin/big-win-component";
import PromptComponent from "slotworks/components/prompt/prompt-component";
import FreespinButtonBehaviour from "slotworks/smart-button/behaviours/freespin-button-behaviour";
import RainbowStacksRecoveryRecordBuilder from "integration/revolver/recovery-record-builder";
import RainbowStacksSpinRecordBuilder from "integration/revolver/spin-record-builder";
import RainbowStacksInit from "states/rs-init";
import LoopWinsDecision from "decisions/loop-wins-decision";
import PickBonus from "states/pick-bonus";
import pickRequestBuilder from "slotworks/integration/revolver/builders/requests/pick-request-builder";
import RainbowStacksRespinRecordBuilder from "integration/revolver/respin-record-builder";
import RainbowStacksBonusRecordBuilder from "integration/revolver/bonus-record-builder";
import LineWinTransitions from "transitions/lines-win-transitions";
import BigWinTransitions from "transitions/big-win-transitions";
import { MultiStyleText } from "slotworks/graphics/pixi/pixi-multistyle-text";
import RainbowStacksEnterFreespins from "states/enter-freespins";
import EnterRespins from "states/enter-respins";
import IsRespinDecision from "decisions/is-respin";
import IsJackpotWonDecision from "decisions/is-jackpot";
import JackpotState from "states/jackpot";
import RecoverRespins from "states/recover-respins";
import Respins from "states/respins";
import InFreespins from "decisions/in-freespins";
import RainbowStacksShowWinLines from "states/show-win-lines";
import RainbowStacksExitFreespins from "states/exit-freespins";
import RainbowStacksEnterRecoveryFreespins from "states/enter-recovery-freespins";
import RespinTransitions from "transitions/respin-transitions";
import RespinIntroTransitions from "transitions/respins-intro-transitions";
import RainbowSpinsGameOver from "states/game-over";
import RainbowStacksSpin from "states/spin";
import RespinAwardTransitions from "transitions/respin-award-transitions";
import RespinGlowTransitions from "transitions/respins-glow-transitions";
import Gameplay from "slotworks/model/gameplay/gameplay-model";
import RainbowStacksAnticipationSupplement from "model/anticipation-supplement";
import PromtsGlowTransition from "transitions/prompts-glow-transition";
import JackpotTransitions from "transitions/jackpot-transition";
import RSBigWins from "states/big-win";
import GlowOverTransitions from "transitions/glow-over-transition";
import RSReelSpinner from "components/matrix/reel-spinner";
import RSShouldExitFreespins from "decisions/should-exit-freespins";
import RSRetriggerFreespins from "states/retrigger-freespins";
import BonusAwardTransition from "transitions/bonus-award-transition";
import RainbowStacksFreespinsRetriggered from "decisions/freespins-retriggered";
import TotalWinTransitions from "transitions/total-win-transitions";
import RainbowStacksBuyoutRecordBuilder from "integration/revolver/buyout-record-builder";
import BuyoutFreespins from "states/buyout-freespins";
import BuyoutBonus from "states/buyout-bonus";
import IsBuyFreeSpinsCheck from "decisions/is-buy-freespins-check";
import IsBuyBonusCheck from "decisions/is-buy-bonus-check";
import BetBarResizer from "slotworks/components/betbar/bet-bar-resizer";

declare const __DEBUG__: boolean;
declare const require: any;
const assetConfig = require("./asset-config.json");
const gameConfig = require("./game-config.json");
const stateConfig = require("./state-config.json");
const soundConfig = require("./sound-config.json");
const componentConfig = require("./component-config.json");
const i18nConfig = require("./i18n-config.json");
const spineConfig = require("./spine-config.json");
const particleConfig = require("./particle-config.json");

require("../static/styles.scss");

if (__DEBUG__) {
    Logger.useDefaults();
}

RevolverInit();

const ready = () => {
    (<any>window).Layers = Layers;
    (<any>window).Components = Components;
    (<any>window).deviceInfo = deviceInfo;
    (<any>window).graphics = graphicsManager;
    (<any>window).campaignsModel = campaignsModel;
    (<any>window).gameState = gameState;
    (<any>window).smart = SmartButton;
    
    setupTransitions();

    MultiStyleText.autoShrink = true;
    MultiStyleText.debugOptions.autoShrink.enabled = true;

    if (__DEBUG__) {
        GameLoop.showStats();
    }
    BetBarResizer.MOBILE_TEXTS_SCALE = true;

    RevolverReady();
    
    BannerComponent.loopTime = 10000;
    ShowWinLines.MULTILINE_ONLY = false;
    FreespinButtonBehaviour.HIDDEN = false;
    
    for (let mask = 0; mask < SmartButtonFlag.MAX; mask++) {
        // Revolver does not have a stop auto spin button during freespins
        if ((mask & SmartButtonFlag.BONUS) > 0) {
            SmartButton.overrideBehaviour(mask, HiddenButtonBehaviour);
        }
    }
    // FooterComponent.layer = Layers.MatrixForeground;

    ButtonElement.DISABLED_TINT = 0xA9A9A9;
    ButtonElement.OVER_TINT = 0xd3d3d3;
    ButtonElement.DOWN_SCALE = 0.8;

    Gameplay.dataProcessorSupplements.addAfterStandard(RainbowStacksAnticipationSupplement)
    RSReelSpinner.anticipationLayer = Layers.Custom.get("Anticipation");
    
    if (!deviceInfo.isDesktop) {
        Layers.LayersToReposition.push(
            Layers.Custom.get('IntroPopups'),
            Layers.Custom.get('Anticipation'),
            // Layers.Prompts,
            Layers.Paylines,
            Layers.SymbolAnimations,
            Layers.MatrixBackground,
            Layers.MatrixContent,
            Layers.Banners,
            Layers.Custom.get('Respin'),
            Layers.CelebrationForeground,
            Layers.CelebrationBackground,
            Layers.Custom.get('RespinForeground')
        );
        Layers.LayersToReset.push(
            Layers.MatrixForeground,
            Layers.Custom.get('BonusBuy'),
            Layers.Custom.get('Feature'),
            Layers.Custom.get('JackpotMeters'),
            Layers.Custom.get('RespinBackground'),
            Layers.Custom.get("RespinMeters"),
            Layers.Custom.get("RespinTexts"),
            Layers.Custom.get("Grass"),
            Layers.Custom.get("BonusBuyPopups"),
            Layers.Custom.get("River"),
        );
        if( Layers.Custom.has('Messages')) {
            Layers.LayersToReset.push(
                Layers.Custom.get('Messages')
            )
        }
    }
};

const setupComms = () => {
    revolverModel.addFreespinIdentifiers(["FREE_SPINS"]);
    RevolverBuilders.init();
    BuilderManager.removeRecordBuilder(SpinRecordBuilder);
    BuilderManager.addRecordBuilder(RainbowStacksSpinRecordBuilder);
    BuilderManager.addRecordBuilder(RainbowStacksRecoveryRecordBuilder);
    BuilderManager.addRecordBuilder(RainbowStacksRespinRecordBuilder);
    BuilderManager.addRecordBuilder(RainbowStacksBonusRecordBuilder);
    BuilderManager.addRequestBuilder(pickRequestBuilder);

    BuilderManager.addRecordBuilder(RainbowStacksBuyoutRecordBuilder);
};

const setupStates = () => {

    StateRegister.add("rs-init", new RainbowStacksInit(StateMachine));
    StateRegister.add("pick-bonus", new PickBonus(StateMachine));
    StateRegister.add("enter-respins", new EnterRespins(StateMachine));
    StateRegister.add("rs-enter-freespins", new RainbowStacksEnterFreespins(StateMachine));
    StateRegister.add("rs-exit-freespins", new RainbowStacksExitFreespins(StateMachine));
    StateRegister.add("rs-enter-recovery-freespins", new RainbowStacksEnterRecoveryFreespins(StateMachine));
    StateRegister.add("jackpot", new JackpotState(StateMachine));
    StateRegister.add("recover-respins", new RecoverRespins(StateMachine));
    StateRegister.add("respins", new Respins(StateMachine));
    StateRegister.add("rs-show-win-lines", new RainbowStacksShowWinLines(StateMachine));
    StateRegister.add("rs-game-over", new RainbowSpinsGameOver(StateMachine));
    StateRegister.add("rs-spin", new RainbowStacksSpin(StateMachine));
    StateRegister.add("rs-big-wins", new RSBigWins(StateMachine));
    StateRegister.add("rs-fs-big-wins", new RSBigWins(StateMachine));
    StateRegister.add("rs-retrigger-freespins", new RSRetriggerFreespins(StateMachine));
    
    StateRegister.add('buyout-freespins', new BuyoutFreespins(StateMachine));
    StateRegister.add('buyout-bonus', new BuyoutBonus(StateMachine));
    StateRegister.add('is-buy-freespins-check', new IsBuyFreeSpinsCheck());
    StateRegister.add('is-buy-bonus-check', new IsBuyBonusCheck());

    StateRegister.add("should-loop-wins", new LoopWinsDecision());
    StateRegister.add("freespins-retriggered-check", new RainbowStacksFreespinsRetriggered());
    
    StateRegister.add("is-respin", new IsRespinDecision());
    StateRegister.add("rs-in-freespins", new InFreespins());
    StateRegister.add("is-jackpot-won", new IsJackpotWonDecision());
    StateRegister.add("rs-should-exit-freespins", new RSShouldExitFreespins());
};

const setupTransitions = () => {
    BigWinComponent.CLICK_TO_SKIP = true;
    PromptComponent.DEFAULT_TIME = 800;

    for (let row = 1; row <= 3; row++) {
        Layers.CelebrationContent.setTransitions(`line_row_${row}_win`, new LineWinTransitions());
    }

    Layers.CelebrationContent.setTransitions("totalWin", new TotalWinTransitions());

    Layers.CelebrationContent.setTransitions("bigWin", new BigWinTransitions());
    Layers.CelebrationContent.setTransitions("megaWin", new BigWinTransitions());
    Layers.CelebrationContent.setTransitions("superMegaWin", new BigWinTransitions());
    Layers.CelebrationContent.setTransitions("epicWin", new BigWinTransitions());
    Layers.CelebrationContent.setTransitions("respins", new RespinGlowTransitions());

    Layers.CelebrationForeground.setTransitions("jackpot", new JackpotTransitions());

    Layers.Custom.get("Respin").setTransitions("respin", new RespinTransitions());

    Layers.Prompts.setTransitions("freespinswin", new PromtsGlowTransition());
    Layers.Prompts.setTransitions("freespinssummary", new PromtsGlowTransition());

    Layers.Prompts.setTransitions("respinsintro", new RespinIntroTransitions());
    Layers.Prompts.setTransitions("respinsintro_fs", new RespinIntroTransitions());
    Layers.Prompts.setTransitions("multiplier", new RespinAwardTransitions());
    Layers.Prompts.setTransitions("multiplier_fs", new RespinAwardTransitions());
    Layers.Prompts.setTransitions("freespin_respin_award", new BonusAwardTransition());

    Layers.Custom.get("Glow").setTransitions("glow", new GlowOverTransitions());
};

setupComms();
setupStates();

SlotWorks.go(gameConfig, stateConfig, assetConfig, componentConfig, soundConfig, i18nConfig, spineConfig, particleConfig, ready);

if (__DEBUG__) {
    EnableOnScreenErrors();
}



// WEBPACK FOOTER //
// ./~/ifdef-loader/ifdef-loader.js??ref--0-1!./src/app.ts